import { ClaimStatus, ClaimStatuses } from '@/api/claims'
import { CheckBox } from '@/components/atoms'
import { DateRangePicker } from '@/components/molecules/DateRangePicker'
import { Flex } from '@/components/styled'
import { Categories as InsuranceCategories } from '@/enums'
import { useOnOutsideClick } from '@/utils/hooks/useOnOutsideClick'
import { Button } from '@surein/ui'
import { FilterIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import CurrentlyAppliedFilters from './CurrentlyAppliedFilters'
import {
  StyledBottomRow,
  StyledDropDown,
  StyledDropdownColumn,
  StyledDropdownContainer,
  StyledDropdownGrid,
} from './styles'

export type FilterState = {
  status: ClaimStatus[]
  insuranceCategories: string[]
  date: DateRange | null
}

type FiltersProps = {
  applyFilters: (filters: {
    status: ClaimStatus[]
    insuranceCategories: string[]
    date: { from: string | undefined; to: string | undefined }
  }) => void
}

type FiltersMode = 'status' | 'date' | 'insurance'

const INITIAL_FILTERS: FilterState = {
  status: [],
  insuranceCategories: [],
  date: null,
}

const Filters = ({ applyFilters }: FiltersProps) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<FiltersMode>('status')

  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const [draftFilters, setDraftFilters] = useState<FilterState>(INITIAL_FILTERS)
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(
    INITIAL_FILTERS,
  )

  const dropdownHeight = useMemo(() => {
    if (activeTab === 'date') {
      return '358px'
    }

    if (activeTab === 'insurance') {
      return '304px'
    }

    return '224px'
  }, [activeTab])

  useEffect(() => {
    if (isOpen) {
      setDraftFilters(appliedFilters)
    }
  }, [isOpen, appliedFilters])

  useOnOutsideClick(dropdownRef, () => setIsOpen(false), [isOpen], [buttonRef])

  const toggleStatus = (status: ClaimStatus) => {
    setDraftFilters((prev) => ({
      ...prev,
      status: prev.status.includes(status)
        ? prev.status.filter((s) => s !== status)
        : [...prev.status, status],
    }))
  }

  const toggleInsuranceCategory = (category: string) => {
    setDraftFilters((prev) => ({
      ...prev,
      insuranceCategories: prev.insuranceCategories.includes(category)
        ? prev.insuranceCategories.filter((c) => c !== category)
        : [...prev.insuranceCategories, category],
    }))
  }

  const propagateFiltersToParent = (filters: FilterState) => {
    applyFilters({
      status: filters.status,
      insuranceCategories: filters.insuranceCategories,
      date: {
        from: filters.date?.from?.toISOString(),
        to: filters.date?.to?.toISOString(),
      },
    })
  }

  const onApply = () => {
    setAppliedFilters(draftFilters)
    propagateFiltersToParent(draftFilters)
    setIsOpen(false)
  }

  const onReset = () => {
    setDraftFilters(INITIAL_FILTERS)
    setAppliedFilters(INITIAL_FILTERS)
    propagateFiltersToParent(INITIAL_FILTERS)
  }

  const onRemoveSingleFilterGroup = (key: keyof FilterState) => {
    const nextFilters = {
      ...appliedFilters,
      [key]: INITIAL_FILTERS[key],
    }

    setAppliedFilters(nextFilters)
    setDraftFilters(nextFilters)
    propagateFiltersToParent(nextFilters)
  }

  return (
    <Flex gap="8px">
      <StyledDropdownContainer>
        <Button
          icon={FilterIcon}
          ref={buttonRef}
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
        >
          {t('claimsPage.filters.title')}
        </Button>
        <StyledDropDown
          ref={dropdownRef}
          isOpen={isOpen}
          height={dropdownHeight}
        >
          <StyledDropdownGrid>
            <StyledDropdownColumn width="120px" padding="8px 12px 0px 16px">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveTab('status')}
              >
                {t('claimsPage.filters.status')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveTab('insurance')}
              >
                {t('claimsPage.filters.insurance')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveTab('date')}
              >
                {t('claimsPage.filters.date')}
              </Button>
            </StyledDropdownColumn>

            <StyledDropdownColumn
              padding="16px 0px 16px 16px"
              width="340px"
              borderLeft
              maxHeight={activeTab === 'date' ? '288px' : '238px'}
            >
              {activeTab === 'status' && (
                <StyledDropdownColumn>
                  {ClaimStatuses.map((status) => (
                    <CheckBox
                      key={status}
                      label={t(`claimsPage.table.statuses.${status}`)}
                      checked={draftFilters.status.includes(
                        status as ClaimStatus,
                      )}
                      onChange={() => toggleStatus(status as ClaimStatus)}
                    />
                  ))}
                </StyledDropdownColumn>
              )}

              {activeTab === 'insurance' && (
                <StyledDropdownColumn>
                  {Object.entries(InsuranceCategories).map(([key, value]) => (
                    <CheckBox
                      key={key}
                      label={t(`categories.${value.key}.name`)}
                      checked={draftFilters.insuranceCategories.includes(key)}
                      onChange={() => toggleInsuranceCategory(key)}
                    />
                  ))}
                </StyledDropdownColumn>
              )}

              {activeTab === 'date' && (
                <DateRangePicker
                  selected={draftFilters.date}
                  setSelected={(date) =>
                    setDraftFilters({ ...draftFilters, date })
                  }
                />
              )}
            </StyledDropdownColumn>
          </StyledDropdownGrid>

          <StyledBottomRow>
            <Button variant="outline" size="sm" onClick={onReset}>
              {t('claimsPage.filters.reset')}
            </Button>
            <Button variant="primary" size="sm" onClick={onApply}>
              {t('claimsPage.filters.apply')}
            </Button>
          </StyledBottomRow>
        </StyledDropDown>
      </StyledDropdownContainer>

      <CurrentlyAppliedFilters
        filters={appliedFilters}
        onRemove={onRemoveSingleFilterGroup}
      />
    </Flex>
  )
}

export default Filters
